import React, { FC, useCallback, useEffect, useState } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import { IonLabel } from '@ionic/react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { setParameter } from '../../actions/setParam';

import classes from './Robots.module.css';
import classNames from 'classnames';
import Messages from './Robots.messages';

import isAuthenticated from '../Authentication/Authenticated';
import { Device } from '../../types/types';
import ListGrid from '../ListGrid/ListGrid';
import RobotName from './RobotName';
import RobotGroupName from './RobotGroupName';
import RobotStatus from './RobotStatus';
import RobotDefaultProps from './RobotDefaultProps';
import RobotMoveGroup from '../RobotMoveGroup/RobotMoveGroup';
import { styled } from '@material-ui/styles';
import { CircularProgress, Divider, MenuItem, Theme, Typography } from '@material-ui/core';
import Disinfection from './Disinfection';

import { useTypedSelector } from '../../reducers';
import { b64EncodeUnicode } from '../../utils/encoding';
import { publish } from '../../actions/publish';
import { formatDistance } from 'date-fns';
import { UVDRobotStatus } from './UVDRobotStatus';
import { UVDBattery } from './UVDBattery';
import { differenceInSeconds } from 'date-fns';
import EllipsisText from '../EllipsisText/EllipsisText';
import RobotTransferModal from '../RobotTransferModal/RobotTransferModal';

interface RobotsProps {
	robotHeaders: Array<Record<string, any>>;
	setSelectedDeviceId: any;
	setIsEditing: any;
	data: Array<any>;
	disinfectionLoader?: any;
}
const StyledMenuItem = styled(MenuItem)(() => ({
	paddingLeft: 10,
	borderRadius: 5,
}));

interface StyledProps {
	theme: Theme;
	isOffline?: boolean;
}

const CellText = styled(Typography)(({ theme, isOffline }: StyledProps) => ({
	color: isOffline ? theme.palette.OP_Grey[40] : theme.palette.OP_Grey[90],
}));

const RobotsList: FC<RobotsProps> = (props: any) => {
	const {
		data,
		setSelectedDeviceId,
		setIsEditing,
		robotHeaders,
		selectRobots,
		selectedRobots,
		handleOnCheck,
		checkAll,
		unCheckAll,
		disinfectionLoader,
	} = props;
	const [pageSize] = useState(11);
	const [showTransfer, setShowTransfer] = useState(false);
	const [showRobotTransfer, setShowRobotTransfer] = useState(false);
	const [selectedRobot, setSelectedRobot] = useState();
	const username = useTypedSelector(state => state.accountState.user.username);
	const encodedUser = b64EncodeUnicode(username);

	const formatTemplate = useCallback(
		(item: any, property: string) => {
			switch (property) {
				case 'robotName':
					return (
						<RobotName
							selectRobots={selectRobots}
							robotId={item.deviceId}
							orgId={item.orgId}
							isOffline={item.isOffline}
						/>
					);
				case 'deviceGroupName':
					return <RobotGroupName robotId={item.deviceId} orgId={item.orgId} />;
				case 'location':
					return (
						<IonLabel>
							<EllipsisText text={item?.location} />
						</IonLabel>
					);
				case 'status':
					return <RobotStatus robotId={item.deviceId} orgId={item.orgId} />;
				case 'uvdstatus':
					if (!item.mongoDevice && !item.decommissioned) {
						return <></>;
					}
					return (
						<UVDRobotStatus
							device={item.mongoDevice}
							isOffline={item.isOffline}
							isDecommissioned={item.decommissioned}
						/>
					);

				case 'battery':
					if (!item.mongoDevice) {
						return <></>;
					}
					return <UVDBattery {...item} isOffline={item.isOffline} />;
				case 'disinfectionCount':
					if (!item.mongoDevice) {
						return <></>;
					}
					if (disinfectionLoader) {
						return <CircularProgress size={16} color="primary" />;
					} else {
						return (
							<Disinfection
								robotId={item.mongoDevice.robotId}
								count={item.disinfectionCount}
								isOffline={item.isOffline}
							/>
						);
					}

				case 'inUseSince':
					if (!item.mongoDevice) {
						return <></>;
					}

					return (
						<CellText isOffline={item.isOffline}>
							{item.mongoDevice.manufacturingDate.getTime() > 0
								? formatDistance(
										new Date(item.mongoDevice.manufacturingDate),
										new Date(),
										{
											addSuffix: true,
										}
								  )
								: ''}
						</CellText>
					);

				case 'lastSeen':
					if (!item.lastSeen) {
						return <></>;
					}
					return (
						<CellText>
							{item.lastSeen}
						</CellText>
					);

				default:
					return (
						<RobotDefaultProps
							robotId={item.deviceId}
							orgId={item.orgId}
							property={property}
						/>
					);
			}
		},
		[selectRobots, disinfectionLoader]
	);

	const onEdit = (device: Device) => {
		if (!device?.decommissioned) {
			setSelectedDeviceId(device.deviceId);
			setIsEditing(true);
		}
	};

	const unassignRobotFromGroup = (device: Device) => {
		publish(`microservice/${device.orgId}/${encodedUser}/updateDeviceGroupDevices`, {
			requestId: 'updateDeviceGroupDevicesId',
			data: {
				deviceGroupId: device.deviceGroupsIds[0],
				remove: [device.deviceId],
			},
		});
	};

	const check = (item: any) => {
		return selectedRobots.some((robot: any) => robot.name === item.name);
	};

	return (
		<>
			<ListGrid
				id="robot-list-grid"
				headers={robotHeaders}
				data={data}
				pageSize={pageSize}
				itemTemplate={(item, property) => {
					if (!item) return;
					return formatTemplate(item, property);
				}}
				moreContent={item => {
					return (
						<>
							<StyledMenuItem
								onClick={() => {
									setShowTransfer(true);
									setSelectedRobot(item);
								}}
							>
								<FormattedMessage {...Messages.moveTo} />
							</StyledMenuItem>
							{!item?.decommissioned && (
								<>
									<Divider />
									<StyledMenuItem
										onClick={() => {
											setShowRobotTransfer(true);
											setSelectedRobot(item);
										}}
									>
										<FormattedMessage
											{...Messages.transferBetweenOrganizations}
										/>
									</StyledMenuItem>
								</>
							)}
							{item?.deviceGroupsIds && item?.deviceGroupsIds?.length > 0 ? (
								<>
									<Divider />
									<StyledMenuItem
										onClick={() => {
											unassignRobotFromGroup(item);
										}}
									>
										<FormattedMessage {...Messages.unassignRobotFromGroup} />
									</StyledMenuItem>
								</>
							) : null}
						</>
					);
				}}
				noDataTemplate={
					<>
						<IonLabel className={classes.noData}>
							<FormattedMessage {...Messages.noRobots} />
						</IonLabel>
						<IonLabel className={classNames(classes.noData, classes.noDataHint)}>
							<FormattedMessage {...Messages.noRobotsHint} />
						</IonLabel>
					</>
				}
				checkAll={() => checkAll(data)}
				handleOnCheck={handleOnCheck}
				unCheckAll={unCheckAll}
				checkItem={check}
				onRowClick={selectRobots ? () => {} : onEdit}
				sortType="SET_DEVICES_SORT_PARAMS"
				listType="Robots"
			/>
			{showTransfer && (
				<RobotMoveGroup
					isOpen={selectedRobot && showTransfer}
					onDismiss={() => {
						setShowTransfer(false);
						setSelectedRobot(undefined);
					}}
					robot={selectedRobot}
				/>
			)}
			{showRobotTransfer && (
				<>
					<RobotTransferModal
						isOpen={true}
						onDismiss={() => setShowRobotTransfer(false)}
						collection={[selectedRobot]}
						unCheckAll={unCheckAll}
					/>
				</>
			)}
		</>
	);
};

const mapStateToProps = (state: any) => ({
	deviceGroups: state.deviceGroupsState,
	selectedOrganizationType: state.selectedOrganizationState.organization.orgType,
	orgId: state.selectedOrganizationState.organization.orgId,
});

const enhance = compose(connect(mapStateToProps, { setParameter }));

export default injectIntl(isAuthenticated(enhance(RobotsList), 'RobotsList'));
